import { keepPreviousData, queryOptions, useQuery } from '@tanstack/react-query'

import { getGisTrends } from '../api/gisApi'

import type { GisScope } from '../api/types'

/**
 * Adaptive-bucket time series for the lens sparklines. The scope (period + entity + custom range)
 * IS in the key — period changes the bucketing, entity/range change the data. `keepPreviousData`
 * keeps the sparkline steady while a new scope loads.
 */
export const gisTrendsQueryOptions = (scope: GisScope) =>
  queryOptions({
    queryKey: ['gis', 'trends', scope.period, scope.entityId ?? null, scope.dateFrom ?? null, scope.dateTo ?? null] as const,
    queryFn: () => getGisTrends(scope),
    staleTime: 1000 * 60 * 2,
    placeholderData: keepPreviousData,
  })

export function useGisTrends(scope: GisScope) {
  return useQuery(gisTrendsQueryOptions(scope))
}

/**
 * Mandal-scoped trends (P2). The full scope is threaded through (so custom range / entity reach the
 * request — the backend rejects `period=custom` without dates), and `mandal` (a mandal_id or name)
 * is IN the key so each mandal caches separately and switching mandals doesn't read a stale series.
 */
export const gisMandalTrendsQueryOptions = (scope: GisScope, mandal: string) =>
  queryOptions({
    queryKey: ['gis', 'trends', scope.period, scope.entityId ?? null, scope.dateFrom ?? null, scope.dateTo ?? null, mandal] as const,
    queryFn: () => getGisTrends({ ...scope, mandal }),
    staleTime: 1000 * 60 * 2,
    placeholderData: keepPreviousData,
  })

export function useGisMandalTrends(scope: GisScope, mandal: string) {
  return useQuery(gisMandalTrendsQueryOptions(scope, mandal))
}
